// Trainee course quiz — pass to earn the certificate. Unlimited retakes.
// One question per screen, 30s timer each, Submit & Next to advance (auto-advances on timeout).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import {
    ListChecks, CheckCircle2, XCircle, Trophy, RotateCcw, ArrowLeft, GraduationCap,
    Loader2, Clock, ChevronRight,
} from 'lucide-react';

const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];
const QUESTION_TIME = 30; // seconds per question

export default function TraineeQuiz() {
    const { courseId } = useParams();
    const navigate = useNavigate();
    const [quiz, setQuiz] = useState(null);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');

    const load = () => {
        setResult(null); setQuiz(null); setError('');
        api.get(`/trainee/courses/${courseId}/quiz`)
            .then((r) => setQuiz(r.data))
            .catch((e) => setError(e?.response?.data?.detail || 'Quiz unavailable'));
    };
    useEffect(load, [courseId]);

    const submitAnswers = async (answers) => {
        const r = await api.post(`/trainee/courses/${courseId}/quiz/submit`, { answers });
        setResult(r.data);
        if (r.data.passed) toast.success(`Passed! ${r.data.score_percent}% — certificate issued 🎉`);
        else toast.error(`Scored ${r.data.score_percent}% — pass mark is ${r.data.pass_percent}%. Try again!`);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    if (error) return (
        <div className="max-w-2xl mx-auto text-center py-16" data-testid="quiz-error">
            <ListChecks className="h-10 w-10 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">{error}</p>
            <Link to="/learn/courses" className="inline-flex items-center gap-1 mt-4 text-sm font-bold text-brand-black"><ArrowLeft className="h-4 w-4" /> Back to Courses</Link>
        </div>
    );
    if (!quiz) return <div className="py-20 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>;

    if (result) return <ResultView result={result} onRetake={load} onBack={() => navigate('/learn/courses')} />;

    return <QuizTaking quiz={quiz} submitAnswers={submitAnswers} />;
}

function QuizTaking({ quiz, submitAnswers }) {
    const total = quiz.questions.length;
    const [current, setCurrent] = useState(0);
    const [answers, setAnswers] = useState({});
    const [timeLeft, setTimeLeft] = useState(QUESTION_TIME);
    const [submitting, setSubmitting] = useState(false);

    const answersRef = useRef({});
    const currentRef = useRef(0);
    const submittingRef = useRef(false);
    useEffect(() => { answersRef.current = answers; }, [answers]);
    useEffect(() => { currentRef.current = current; }, [current]);

    const q = quiz.questions[current];
    const selected = answers[q.id];
    const isLast = current === total - 1;

    const doSubmit = useCallback(async () => {
        if (submittingRef.current) return;
        submittingRef.current = true;
        setSubmitting(true);
        try {
            await submitAnswers(answersRef.current);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Submission failed');
            submittingRef.current = false;
            setSubmitting(false);
        }
    }, [submitAnswers]);

    const advance = useCallback(() => {
        const cur = currentRef.current;
        if (cur >= total - 1) doSubmit();
        else setCurrent(cur + 1);
    }, [total, doSubmit]);

    // Per-question 30s countdown — resets on question change; auto-advances on expiry.
    useEffect(() => {
        setTimeLeft(QUESTION_TIME);
        const startedAt = Date.now();
        const t = setInterval(() => {
            const left = QUESTION_TIME - (Date.now() - startedAt) / 1000;
            if (left <= 0) {
                clearInterval(t);
                setTimeLeft(0);
                advance();
            } else {
                setTimeLeft(left);
            }
        }, 200);
        return () => clearInterval(t);
    }, [current, advance]);

    const pick = (oi) => setAnswers((p) => ({ ...p, [q.id]: oi }));

    const timePct = (timeLeft / QUESTION_TIME) * 100;
    const danger = timeLeft <= 8;
    const progressPct = Math.round(((current) / total) * 100);

    return (
        <div className="max-w-2xl mx-auto" data-testid="quiz-take">
            <Link to="/learn/courses" className="inline-flex items-center gap-1 text-sm font-bold text-white/70 hover:text-white mb-3"><ArrowLeft className="h-4 w-4" /> Courses</Link>

            {/* Header */}
            <div className="bg-gradient-to-br from-brand-black to-slate-800 text-white rounded-3xl p-6">
                <div className="text-[11px] font-bold uppercase tracking-widest text-amber-300 inline-flex items-center gap-1.5"><ListChecks className="h-3.5 w-3.5" /> Course Quiz</div>
                <h1 className="font-display font-extrabold text-2xl mt-1">{quiz.course_title}</h1>
                <p className="text-sm text-slate-300 mt-1">{quiz.num_questions} questions · pass mark <b className="text-amber-300">{quiz.pass_percent}%</b> · unlimited retakes</p>
            </div>

            {/* Progress + timer */}
            <div className="mt-5 flex items-center justify-between gap-3">
                <span className="text-sm font-bold text-white" data-testid="quiz-progress">Question {current + 1} of {total}</span>
                <span className={`inline-flex items-center gap-1.5 text-sm font-extrabold ${danger ? 'text-rose-500' : 'text-white'}`} data-testid="quiz-timer">
                    <Clock className="h-4 w-4" /> {Math.ceil(timeLeft)}s
                </span>
            </div>
            <div className="mt-2 h-2 bg-slate-200 rounded-full overflow-hidden">
                <div className={`h-full transition-all duration-200 ${danger ? 'bg-rose-500' : 'bg-brand-yellow'}`} style={{ width: `${timePct}%` }} />
            </div>
            <div className="mt-1 h-1 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-400 transition-all duration-300" style={{ width: `${progressPct}%` }} />
            </div>

            {/* Question card */}
            <div key={q.id} className="mt-4 bg-white rounded-2xl ring-1 ring-slate-200 p-5" data-testid={`quiz-question-${current}`}>
                <div className="font-bold text-slate-900 text-base leading-snug">{current + 1}. {q.question}</div>
                {q.image_url && (
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${q.image_url}`} alt="" className="mt-3 w-full max-h-72 object-contain rounded-xl ring-1 ring-slate-200 bg-slate-50" data-testid="quiz-question-image" />
                )}
                <div className="mt-4 space-y-2.5">
                    {q.options.map((o, oi) => {
                        const isSel = selected === oi;
                        return (
                            <button key={oi} onClick={() => pick(oi)}
                                className={`w-full text-left flex items-center gap-3 px-3.5 py-3 rounded-xl ring-1 text-sm transition ${isSel ? 'ring-2 ring-brand-yellow bg-amber-50 font-bold text-slate-900' : 'ring-slate-200 text-slate-700 hover:bg-slate-50'}`}
                                data-testid={`quiz-${current}-opt-${oi}`}>
                                <span className={`w-6 h-6 rounded-full grid place-items-center text-[11px] font-bold flex-shrink-0 ${isSel ? 'bg-brand-yellow text-brand-black' : 'bg-slate-200 text-slate-600'}`}>{LETTERS[oi]}</span>
                                {o}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Submit & Next */}
            <div className="sticky bottom-4 mt-5">
                <button onClick={advance} disabled={submitting}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-3.5 rounded-2xl bg-brand-yellow text-brand-black font-extrabold shadow-lg disabled:opacity-50 hover:brightness-105 transition"
                    data-testid="quiz-submit-next">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" />
                        : isLast ? <><CheckCircle2 className="h-4 w-4" /> Submit quiz</>
                            : <>Submit &amp; Next <ChevronRight className="h-4 w-4" /></>}
                </button>
                {selected === undefined && <p className="text-center text-[11px] text-white/60 mt-2">Pick an option, or it auto-advances when the timer runs out.</p>}
            </div>
        </div>
    );
}

function ResultView({ result, onRetake, onBack }) {
    return (
        <div className="max-w-2xl mx-auto" data-testid="quiz-result">
            <div className={`rounded-3xl p-7 text-center text-white ${result.passed ? 'bg-gradient-to-br from-emerald-500 to-teal-600' : 'bg-gradient-to-br from-rose-500 to-rose-700'}`}>
                {result.passed ? <Trophy className="h-12 w-12 mx-auto mb-2" /> : <XCircle className="h-12 w-12 mx-auto mb-2" />}
                <div className="font-display font-extrabold text-4xl">{result.score_percent}%</div>
                <div className="text-sm opacity-90 mt-1">{result.correct} / {result.total} correct · pass mark {result.pass_percent}%</div>
                <div className="mt-3 font-bold text-lg">{result.passed ? 'Passed — you earned your certificate!' : 'Not passed yet — give it another go.'}</div>
            </div>

            {result.passed && result.certificate && (
                <div className="mt-4 bg-white rounded-2xl ring-1 ring-emerald-200 p-5 flex items-center gap-4" data-testid="quiz-cert">
                    <div className="h-12 w-12 rounded-2xl bg-emerald-50 grid place-items-center text-emerald-600"><GraduationCap className="h-6 w-6" /></div>
                    <div className="flex-1">
                        <div className="font-display font-bold text-slate-900">{result.certificate.course_title}</div>
                        <div className="text-xs text-slate-500">Certificate ID: {result.certificate.certificate_id}</div>
                    </div>
                    <Link to="/learn/certificates" className="px-3 py-2 rounded-xl bg-emerald-500 text-white text-xs font-bold" data-testid="quiz-view-certificate">View Certificate</Link>
                </div>
            )}

            <div className="mt-5 flex gap-2">
                <button onClick={onRetake} className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-brand-yellow text-brand-black font-bold text-sm" data-testid="quiz-retake">
                    <RotateCcw className="h-4 w-4" /> Retake quiz
                </button>
                <button onClick={onBack} className="flex-1 px-4 py-2.5 rounded-xl bg-slate-100 text-slate-700 font-bold text-sm">Back to Courses</button>
            </div>

            {/* Answer review */}
            <h3 className="font-display font-bold text-white mt-7 mb-3">Answer review</h3>
            <div className="space-y-3">
                {result.results.map((r, i) => (
                    <div key={r.question_id} className="bg-white rounded-2xl ring-1 ring-slate-200 p-4" data-testid={`review-${i}`}>
                        <div className="flex items-start gap-2">
                            {r.correct ? <CheckCircle2 className="h-5 w-5 text-emerald-500 mt-0.5 shrink-0" /> : <XCircle className="h-5 w-5 text-rose-500 mt-0.5 shrink-0" />}
                            <div className="flex-1">
                                <div className="font-bold text-slate-800 text-sm">{i + 1}. {r.question}</div>
                                <ul className="mt-2 space-y-1">
                                    {r.options.map((o, oi) => {
                                        const isCorrect = oi === r.correct_index;
                                        const isYours = oi === r.your_index;
                                        return (
                                            <li key={oi} className={`text-xs flex items-center gap-2 px-2 py-1 rounded-lg ${isCorrect ? 'bg-emerald-50 text-emerald-700 font-bold' : isYours ? 'bg-rose-50 text-rose-700' : 'text-slate-600'}`}>
                                                <span className="w-4 h-4 rounded-full grid place-items-center text-[9px] bg-white ring-1 ring-slate-200">{LETTERS[oi]}</span>
                                                {o}
                                                {isCorrect && <CheckCircle2 className="h-3 w-3" />}
                                                {isYours && !isCorrect && <span className="text-[10px]">(your answer)</span>}
                                            </li>
                                        );
                                    })}
                                </ul>
                                {r.explanation && <div className="mt-1.5 text-[11px] text-slate-500 italic">{r.explanation}</div>}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
